import React, { useState } from 'react';
import { generatePlantsForDateRange, GeneratePlantsResponse } from '../../services/gardenService';
import './Garden.css';

interface PlantGenerationDialogProps {
  userId: number;
  focusedYear?: number | null;
  onClose: () => void;
  onComplete: () => void;
}

const PlantGenerationDialog: React.FC<PlantGenerationDialogProps> = ({ userId, focusedYear, onClose, onComplete }) => {
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<GeneratePlantsResponse['summary'] | null>(null);

  // Set default dates based on focused year or fallback to today and 30 days ago
  React.useEffect(() => {
    const today = new Date();
    
    if (focusedYear) {
      // Use the focused year: January 1 to December 31 of that year
      const yearStart = new Date(focusedYear, 0, 1);
      const yearEnd = new Date(focusedYear, 11, 31);
      
      // Don't allow dates in the future
      const maxDate = today < yearEnd ? today : yearEnd;
      
      setStartDate(yearStart.toISOString().split('T')[0]);
      setEndDate(maxDate.toISOString().split('T')[0]);
    } else {
      // Fallback to default: today and 30 days ago
      const thirtyDaysAgo = new Date();
      thirtyDaysAgo.setDate(today.getDate() - 30);
      
      setEndDate(today.toISOString().split('T')[0]);
      setStartDate(thirtyDaysAgo.toISOString().split('T')[0]);
    }
  }, [focusedYear]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!startDate || !endDate) {
      setError('Please select both start and end dates');
      return;
    }

    const start = new Date(startDate);
    const end = new Date(endDate);

    if (start > end) {
      setError('Start date must be before or equal to end date');
      return;
    }

    // Warn if date range is very large
    const daysDiff = Math.ceil((end.getTime() - start.getTime()) / (1000 * 60 * 60 * 24));
    if (daysDiff > 365) {
      if (!window.confirm(`You are about to generate plants for ${daysDiff} days. This may take a while. Continue?`)) {
        return;
      }
    }

    setLoading(true);
    setError(null);
    setResult(null);

    try {
      const response = await generatePlantsForDateRange({
        user_id: userId,
        start_date: startDate,
        end_date: endDate
      });

      if (response.success) {
        setResult(response.summary || null);
        // Refresh garden data after a short delay
        setTimeout(() => {
          onComplete();
        }, 2000);
      } else {
        setError(response.message || 'Failed to generate plants');
      }
    } catch (err) {
      console.error('Error generating plants:', err);
      setError('Failed to generate plants');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="garden-generation-dialog-overlay" onClick={onClose}>
      <div className="garden-generation-dialog" onClick={(e) => e.stopPropagation()}>
        <div className="garden-generation-dialog-header">
          <h2 className="garden-generation-dialog-title">Generate Plants</h2>
          <button
            className="garden-generation-dialog-close"
            onClick={onClose}
            disabled={loading}
          >
            ×
          </button>
        </div>

        {result ? (
          <div className="garden-generation-dialog-result">
            <p className="garden-generation-dialog-success">Plants generated successfully!</p>
            <div className="garden-generation-dialog-summary">
              <div className="garden-generation-dialog-summary-item">
                <span className="garden-generation-dialog-summary-label">Created:</span>
                <span className="garden-generation-dialog-summary-value">{result.created}</span>
              </div>
              <div className="garden-generation-dialog-summary-item">
                <span className="garden-generation-dialog-summary-label">Updated:</span>
                <span className="garden-generation-dialog-summary-value">{result.updated}</span>
              </div>
              <div className="garden-generation-dialog-summary-item">
                <span className="garden-generation-dialog-summary-label">Skipped:</span>
                <span className="garden-generation-dialog-summary-value">{result.skipped}</span>
              </div>
            </div>
            <button
              className="garden-generation-dialog-button"
              onClick={onClose}
            >
              Close
            </button>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="garden-generation-dialog-form">
            <p className="garden-generation-dialog-description">
              Select a date range to generate plants. Plants will be created for days with 
              activity (tasks, habits, or scheduled blocks).
            </p>

            <div className="garden-generation-dialog-fields">
              <div className="garden-generation-dialog-field">
                <label htmlFor="start-date" className="garden-generation-dialog-label">
                  Start Date
                </label>
                <input
                  id="start-date"
                  type="date"
                  value={startDate}
                  onChange={(e) => setStartDate(e.target.value)}
                  className="garden-generation-dialog-input"
                  max={endDate || new Date().toISOString().split('T')[0]}
                  required
                />
              </div>

              <div className="garden-generation-dialog-field">
                <label htmlFor="end-date" className="garden-generation-dialog-label">
                  End Date
                </label>
                <input
                  id="end-date"
                  type="date"
                  value={endDate}
                  onChange={(e) => setEndDate(e.target.value)}
                  className="garden-generation-dialog-input"
                  max={new Date().toISOString().split('T')[0]}
                  required
                />
              </div>
            </div>

            {error && (
              <div className="garden-generation-dialog-error">{error}</div>
            )}

            {loading && (
              <div className="garden-generation-dialog-loading">
                Generating plants... This may take a moment.
              </div>
            )}

            <div className="garden-generation-dialog-actions">
              <button
                type="button"
                className="garden-generation-dialog-button garden-generation-dialog-button-secondary"
                onClick={onClose}
                disabled={loading}
              >
                Cancel
              </button>
              <button
                type="submit"
                className="garden-generation-dialog-button"
                disabled={loading}
              >
                {loading ? 'Generating...' : 'Generate Plants'}
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
};

export default PlantGenerationDialog;

